/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo } from 'react';
import gitlab_logo from '@/assets/img/gitlab-logo-500-rgb.png';
import keycloak_logo from '@/assets/img/keycloak-logo-100.png';
import { Divider } from 'primereact/divider';
import { useLocation } from 'react-router';
import { AppRoutes } from '@/constants/routes';
import { HeaderNavbar } from '@/components/HeaderNavbar';
import { useTranslation } from 'react-i18next';
import { useStoreContext } from '@/contexts/StoreContext';
import { useLocalStorage } from 'primereact/hooks';

export const LoginPage: React.FC = () => {
  const { pathname, origin } = window.location;
  const router = useLocation();
  const { antflowProcessApiEndpoint, antflowServerConfig } = useStoreContext();

  const { t } = useTranslation();
  const [, setOffline] = useLocalStorage(false, 'offline');

  const redirectUrl = useMemo(() => {
    const host_pathname = pathname.substring(1);
    const router_pathname = router.pathname;

    const router_has_redirect = !!router_pathname && router_pathname !== '';
    let redirect_to_ui = `${origin}/${host_pathname ?? ''}`;

    if (router_has_redirect && router_pathname !== AppRoutes.login.path) {
      redirect_to_ui = `${redirect_to_ui}?redirect=${router_pathname.concat(router.search)}`;
    }

    return redirect_to_ui;
  }, [pathname, origin, router]);

  const GitLabIcon = () => <img src={gitlab_logo} className="inline-block" alt="GitLab" height={25} />;
  const KeycloakIcon = () => <img src={keycloak_logo} className="inline-block" alt="Keycloak" height={30} />;

  const handleLogin = () => {
    setOffline(false);
    antflowProcessApiEndpoint.login(redirectUrl);
  };
  return (
    <div className="w-full h-screen overflow-hidden">
      <HeaderNavbar />
      <div className="w-full h-full flex justify-content-center align-items-center pb-8">
        <div className="w-3 mb-8">
          <div className="w-full shadow-4 p-6 h-22rem flex flex-column  justify-content-between">
            <div>
              <div className="text-2xl font-bold">{t(['login.connexion', 'Connexion'])}</div>

              <Divider align="center">
                <div className="text-center text-gray-500 mt-4">{t('login.title', '')}</div>
              </Divider>
            </div>

            <div className="flex flex-column align-items-center justify-content-center gap-2 mt-2">
              {antflowServerConfig?.oidc.provider === 'keycloak' ? (
                <button
                  onClick={handleLogin}
                  className="text-center no-underline font-semibold flex w-full justify-content-center p-button p-component p-button-outlined p-button-contrast p-button-raised"
                >
                  <KeycloakIcon />
                  <span className=" ml-2 inline-blocktext-center">
                    {t('login.provider.keycloak', 'Login with Keycloak')}
                  </span>
                </button>
              ) : (
                <button
                  onClick={handleLogin}
                  className="text-center no-underline font-semibold flex w-full justify-content-center p-button p-component p-button-outlined p-button-contrast p-button-raised"
                >
                  <GitLabIcon />
                  <span className=" ml-2 inline-blocktext-center">
                    {t('login.provider.gitlab', 'Login with GitLab')}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
